import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig } from '../../../app.settings';
import { Monitor } from '../../models/entities/monitor.type';
import { PluralAssignment } from '../../models/view-models/monitor-models/monitor-assignment.type';
import { MonitorKey } from '../../models/view-models/monitor-models/monitor-key.type';
import { MonitorStartOption } from '../../models/view-models/monitor-models/monitor-start-option.model';
import { ShareContentToPublicParams } from '../../models/entities/share-content-to-public-params.model';

@Injectable()
export class MonitorsService {

  public connectedMonitors: BehaviorSubject<Monitor[]> = new BehaviorSubject([]);
  public allMonitors: BehaviorSubject<Monitor[]> = new BehaviorSubject([]);
  private endpoint: string;

  constructor(public http: HttpClient, public config: AppConfig) {
    this.endpoint = `${this.config.getConfig('api')}Monitor`;
  }

  getMonitors(): Observable<Monitor[]> {
    return this.http.get<Monitor[]>(this.endpoint)
      .pipe(map((x: Monitor[]) => {
        this.connectedMonitors.next(x);
        return x;
      }));
  }

  getMonitorByMonitorKey(monitorKey: MonitorKey): Observable<Monitor> {
    return this.http.get<Monitor>(`${this.endpoint}/${monitorKey.MachinePhysicalAddress}/${monitorKey.MonitorHandler}`,
      { headers: new HttpHeaders({ 'No-Auth': 'True' }) });
  }

  getAllMonitors(): Observable<Monitor[]> {
    return this.http.get<Monitor[]>(`${this.endpoint}/all-monitors`).pipe(
      map((monitors: Monitor[]) => {
        this.allMonitors.next(monitors);
        return monitors;
      }));
  }

  getAvailableUserMonitors(userId: string): Observable<Monitor[]> {
    const params: HttpParams = new HttpParams()
      .set('userId', userId);

    return this.http.get<Monitor[]>(`${this.endpoint}/available-user-monitors`, { params });
  }

  getAnonymousParameter(physicalAddress: string, handler: string, anonymous: boolean = false): Observable<boolean> {
    const headers = anonymous
      ? { headers: new HttpHeaders({ 'No-Auth': 'True' }) }
      : {};

    const params: HttpParams = new HttpParams()
      .set('physicalAddress', physicalAddress)
      .set('handler', handler);

    return this.http.get<boolean>(`${this.endpoint}/get-anonymous-parameter`, { ...headers, params });
  }

  setAnonymousParameter(params: ShareContentToPublicParams): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/set-anonymous-parameter`, params);
  }

  assignPlaylist(screenId: string, machinePhysicalAddress: string, idPlaylist: number) {
    const assignment = new PluralAssignment([new MonitorKey(screenId, machinePhysicalAddress)], idPlaylist);
    return this.http.post(`${this.endpoint}/Assign`, assignment);
  }

  assignAndPlayPlaylist(screenId, machinePhysicalAddress, idPlaylist) {
    const url = this.endpoint + '/AssignAndPlay';
    const assignment = new PluralAssignment([new MonitorKey(screenId, machinePhysicalAddress)], idPlaylist);
    return this.http.post(url, assignment, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  dismissAssignment(monitorKeys: MonitorKey[]): Observable<void> {
    const url = this.endpoint + '/Assign';
    const assignments: PluralAssignment = new PluralAssignment([], null);

    assignments.MonitorKeys.push(
      ...monitorKeys
    );

    return this.http.post<void>(url, assignments, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  assignPlaylistToPlural(assignments: PluralAssignment) {
    const url = this.endpoint + '/Assign';
    return this.http.post(url, assignments, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  updateAndPlayAssignments(assignments: PluralAssignment) {
    const url = this.endpoint + '/AssignAndPlay';
    return this.http.post(url, assignments, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  updateTitle(MonitorHandler: string, MachinePhysicalAddress: string, Title: string): Observable<any> {
    const model = {
      MachinePhysicalAddress,
      MonitorHandler,
      Title
    };
    const url = this.endpoint + '/UpdateMonitorTitle';
    return this.http.post(url, model, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }).pipe(
      map(() => {
        const currentMonitor = this.allMonitors.value.find((monitor: Monitor) =>
          monitor.MonitorHandler === MonitorHandler && monitor.MachinePhysicalAddress === MachinePhysicalAddress);

        if (currentMonitor) {
          currentMonitor.MonitorTitle = Title;
        }
      })
    );
  }

  updateStartOptions(monitorStartOptions: MonitorStartOption[]) {
    const url = this.endpoint + '/UpdateMonitorStartOptions';
    return this.http.post(url, monitorStartOptions, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }


  enableIdentifyScreens(keys: MonitorKey[], showScreenNameOnly: boolean) {
    const endpoint = this.endpoint + '/enable-identify-screens';
    const params: HttpParams = new HttpParams().set('showScreenNameOnly', showScreenNameOnly);

    return this.http.post(endpoint, keys, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      params
    });
  }

  disableIdentifyScreens() {
    const endpoint = this.endpoint + '/disable-identify-screens';
    return this.http.post(endpoint, null, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }
}
